import React from 'react';

import { Redirect } from 'expo-router';
import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { SafeAreaView, ScrollView, StyleSheet, View } from 'react-native';

import { modalHeaderStyles } from 'components/card-modal-header';
import { Header } from 'components/ui';
import Totals from 'experiments/checkout-v2/treatment/features/checkout/components/totals';
import { AddPaymentMethodModal } from 'experiments/checkout-v2/treatment/features/payment/components/add-payment-method-modal';
import { AddReloadPrepaidMethodModal } from 'experiments/checkout-v2/treatment/features/payment/components/add-reload-prepaid-method-modal';
import { usePreviousScreenName } from 'navigation/hooks/use-previous-screen-name';
import { primitive } from 'styles/constants/primitives';
import { isAndroid } from 'utils/environment';
import { routes } from 'utils/routing';

import { CheckoutProvider } from '../checkout-provider';
import { Disclaimers, PaymentSelector } from '../components';
import { CheckoutErrorsModal } from '../components/checkout-errors-modal';
import { CheckoutFooter } from '../components/checkout-footer';
import { OrderPriceChangeToast } from '../components/order-price-change-toast';
import { ServiceModeWidget } from '../components/service-mode';
import { StoreLocatorWithMapWidget } from '../components/store-locator-with-map';
import { TipSelector } from '../components/tip-selector';
import { useCheckoutContext } from '../hooks/use-checkout-context';
import { useCheckoutInit } from '../hooks/use-checkout-init';

const CheckoutHeader = () => {
  const { formatMessage } = useIntl();
  const previousScreenName = usePreviousScreenName();
  const router = useRouter();

  if (!previousScreenName.includes('cart')) {
    return <Redirect href={routes.cart} />;
  }

  const handleOnDismiss = () => {
    router.dismiss();
  };

  return (
    // FIX: this is a hack to fix the header padding on android
    // why we don't use same header component as in cart?
    <View style={{ paddingTop: isAndroid ? modalHeaderStyles.headerContainer.padding : 0 }}>
      <Header
        title={formatMessage({ id: 'checkout' })}
        closeIconVariant="back"
        closeIconPosition="left"
        onCloseIconClick={handleOnDismiss}
      />
    </View>
  );
};

/**
 * It is used to display all the widgets for the checkout page.
 */
const CheckoutPage = () => {
  useCheckoutInit();
  const { serverOrder, isDeliveryOrder } = useCheckoutContext();

  return (
    <>
      <SafeAreaView style={{ flex: 0, backgroundColor: primitive.bk.$bbqBrown }} />
      <View style={styles.container}>
        <CheckoutHeader />
        <ScrollView>
          <View style={styles.scroller}>
            <StoreLocatorWithMapWidget />
            {!isDeliveryOrder && <ServiceModeWidget />}
            <PaymentSelector />
            {isDeliveryOrder && <TipSelector />}
            {/* note that this one is 100% cart dependent  */}
            <Totals />

            <Disclaimers />
            {/* TODO: treat this one as global modal, should be moved to appModals */}
            <AddPaymentMethodModal />
            <AddReloadPrepaidMethodModal />
          </View>
        </ScrollView>
        <CheckoutFooter />
        <CheckoutErrorsModal />
      </View>
      {!isDeliveryOrder && <OrderPriceChangeToast serverOrder={serverOrder} />}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Styles.color.white,
  },
  scroller: {
    flex: 1,
    paddingHorizontal: primitive.spacing.$4,
    gap: primitive.spacing.$4,
    paddingVertical: primitive.spacing.$4,
    width: '100%',
    maxWidth: 560,
    margin: 'auto',
  },
});

const ProvidedCheckout = () => (
  <CheckoutProvider>
    <CheckoutPage />
  </CheckoutProvider>
);

export default ProvidedCheckout;
